import { marked } from 'marked';

import { DOMParser as PMDomParser } from '@tiptap/pm/model';
import { type Editor } from '@tiptap/react';

const DOM_PARSER = new DOMParser();

interface parseContentFromSchemaProps {
  content?: string;
  editor: Editor;
}

function escapeAttr(s: string) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function parseContentFromSchema({ content, editor }: parseContentFromSchemaProps) {
  try {
    if (!content) return '';

    // 1. Parse markdown to HTML
    let html = marked.parse(content, { async: false });
    if (!html) return '';

    // 2) Regex: get `{{ variable }}` or `{{ variable | default('value') }}` (default content in defRaw)
    const mentionRegex = /\{\{\s*([a-zA-Z0-9_]+)(?:\s*\|\s*default\(\s*([^)]*?)\s*\))?\s*\}\}/g;

    html = html.replace(mentionRegex, (_match, mention, defRaw) => {
      const mentionEsc = escapeAttr(mention);

      if (!defRaw) {
        // Return no default
        return `<span class="mention" mention="${mentionEsc}" data-mention="${mentionEsc}" data-default="">{{${mention}}}</span>`;
      }

      // Decode HTML
      const decoded = DOM_PARSER.parseFromString(defRaw, 'text/html').body.textContent ?? defRaw;

      // Remove wrapped `"" ''`
      const stripped = decoded.replace(/^['"]|['"]$/g, '');

      const defEsc = escapeAttr(stripped);

      // Return with default
      return `<span class="mention" mention="${mentionEsc}" default="${defEsc}" data-mention="${mentionEsc}" data-default="${defEsc}">{{${mention}|default('${defEsc}')}}</span>`;
    });

    // 3) Parse HTML to ProseMirror doc using editor's schema
    const { body } = DOM_PARSER.parseFromString(html, 'text/html');
    return PMDomParser.fromSchema(editor.schema).parse(body);
  } catch {
    return '';
  }
}
